/**
 * Application footer component
 * Displays copyright and site branding
 * Uses walrus dark theme colors
 *
 * @module Footer
 */

import { logger } from '../../utils/logger'

/**
 * Footer component props
 */
interface FooterProps {
  /**
   * Optional CSS class name for additional styling
   */
  className?: string
}

/**
 * Footer component
 * Displays copyright information and "Rodrigo the Walrus" branding
 */
export function Footer({ className = '' }: FooterProps) {
  const currentYear = new Date().getFullYear()

  logger.debug('[WALRUS] Footer: rendering', { currentYear })

  return (
    <footer
      className={`bg-walrus-cream border-t border-walrus-gray mt-auto ${className}`}
      role="contentinfo"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          {/* Copyright Text */}
          <div className="text-center md:text-left">
            <p className="text-walrus-silver text-sm">
              &copy; {currentYear} Rodrigo the Walrus. All rights reserved.
            </p>
          </div>

          {/* Branding */}
          <div className="text-center">
            <p className="text-walrus-black font-display font-bold text-base">
              Rodrigo the Walrus
            </p>
            <p className="text-walrus-silver text-xs">Family First</p>
          </div>

          {/* TODO: Add future footer links sections */}
          {/* Future sections could include:
             * - Events (upcoming photo events, exhibitions)
             * - Store (merchandise, prints for sale)
             * - Connect (social media links)
             * - About (project info, contact)
             *
             * Example structure:
             * <div className="flex flex-col space-y-4">
             *   <div>
             *     <h3 className="text-walrus-white font-semibold mb-2">Events</h3>
             *     <ul className="space-y-1">
             *       <li><a href="#" className="text-walrus-silver hover:text-walrus-white text-sm">Upcoming</a></li>
             *       <li><a href="#" className="text-walrus-silver hover:text-walrus-white text-sm">Past</a></li>
             *     </ul>
             *   </div>
             *   <div>
             *     <h3 className="text-walrus-white font-semibold mb-2">Store</h3>
             *     <ul className="space-y-1">
             *       <li><a href="#" className="text-walrus-silver hover:text-walrus-white text-sm">Prints</a></li>
             *       <li><a href="#" className="text-walrus-silver hover:text-walrus-white text-sm">Merchandise</a></li>
             *     </ul>
             *   </div>
             * </div>
          */}

          {/* Placeholder for future links */}
          <div className="text-walrus-silver text-xs">
            <p className="opacity-50">More features coming soon</p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-walrus-gray mt-8 pt-8">
          <p className="text-walrus-silver text-xs text-center">
            Built with care and curiosity
          </p>
        </div>
      </div>
    </footer>
  )
}
